import React from 'react';
import { Ban } from 'lucide-react';
import type { FermetureJour } from '../../lib/reservationsReglagesApi';

/**
 * Sélecteur de service (L06-01 / B3) : « Midi », « Soir »… filtrent la liste, les totaux, le bandeau « table à libérer » et le
 * résumé. À côté de chaque service, le bouton « ⦸ » ferme ce service pour le jour affiché (fermeture « service » du serveur ;
 * les autres fermetures — créneaux, tailles de groupe, réglages du jour — arrivent avec E) ; fermé, il devient « Rouvrir ».
 * Le service est toujours annoncé en toutes lettres : l'état « fermé » ne tient jamais à la couleur seule.
 */
export interface ServiceBouton { id: string; nom: string; heure_debut: string; heure_fin: string }
interface Props {
    services: ServiceBouton[];
    /** '' = tous, un identifiant de service, ou 'hors' (réservations hors des horaires des services). */
    choix: string;
    onChoix: (id: string) => void;
    /** Couverts et réservations par service (hors annulées, absentes, liste d'attente). */
    compte: (id: string) => { n: number; couverts: number };
    horsService: number;
    fermetures: FermetureJour[];
    peutFermer: boolean;
    onFermer: (s: ServiceBouton) => void;
}

const base = 'h-10 max-lg:h-11 px-4 rounded-full border text-[13px] font-semibold inline-flex items-center gap-1.5 shrink-0';
const actif = 'bg-content text-surface border-transparent';
const inactif = 'bg-surface text-content border-border hover:border-content';

export const SelecteurService: React.FC<Props> = ({ services, choix, onChoix, compte, horsService, fermetures, peutFermer, onFermer }) => {
    // Un service « restreint » : des créneaux, des tailles de groupe, une zone ou un rythme du jour sont posés (sans que le service entier soit fermé).
    const restreint = (id: string) => fermetures.some((x) => x.type !== 'service' && (x.resa_service_id === id || x.resa_service_id === null));
    const fermeture = (id: string) => fermetures.find((f) => f.type === 'service' && (f.resa_service_id === id || f.resa_service_id === null));
    const total = services.reduce((s, x) => ({ n: s.n + compte(x.id).n, couverts: s.couverts + compte(x.id).couverts }), { n: 0, couverts: 0 });
    return (
        <div role="group" aria-label="Service affiché" className="flex items-center gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden lg:flex-wrap lg:overflow-visible">
            {services.length > 1 && <button type="button" aria-pressed={choix === ''} onClick={() => onChoix('')} className={`${base} ${choix === '' ? actif : inactif}`}>Tous <span className="font-normal opacity-80">· {total.couverts} couv.</span></button>}
            {services.map((s) => {
                const c = compte(s.id);
                const f = fermeture(s.id);
                return (
                    <span key={s.id} className="inline-flex items-center gap-2 shrink-0">
                        {/* Le service et son bouton « ⦸ » forment une seule pastille collée, mais deux clics distincts : choisir le service / fermer le service. */}
                        <span className="inline-flex items-stretch">
                        <button type="button" aria-pressed={choix === s.id} onClick={() => onChoix(s.id)} className={`${base} ${choix === s.id ? actif : inactif} ${peutFermer ? '!rounded-r-none' : ''}`}>
                            {s.nom}{f && ' (fermé)'} <span className="font-normal opacity-80">· {c.couverts} couv.</span>
                        </button>
                        {peutFermer && (
                            <button type="button" aria-label={`${f ? 'Rouvrir' : 'Afficher complet'} : ${s.nom}`} title={`${f ? 'Rouvrir' : 'Afficher complet'} : ${s.nom}`} onClick={() => onFermer(s)}
                                className="h-10 w-10 max-lg:h-11 max-lg:w-11 rounded-r-full border border-l-0 border-border bg-surface text-content-muted hover:text-state-danger hover:border-state-danger inline-flex items-center justify-center"><Ban size={16} /></button>
                        )}
                        </span>
                        {(f || restreint(s.id)) && <span className={`text-[11.5px] font-semibold whitespace-nowrap ${f ? 'text-state-danger' : 'text-state-warn'}`}>{f ? '✕ Complet' : '⚠ Restreint'}</span>}
                    </span>
                );
            })}
            {horsService > 0 && <button type="button" aria-pressed={choix === 'hors'} onClick={() => onChoix('hors')} className={`${base} ${choix === 'hors' ? actif : inactif}`}>Hors service <span className="font-normal opacity-80">· {horsService}</span></button>}
        </div>
    );
};
